import { useRouter } from 'next/router';
import React, { useEffect } from 'react';

import { ROUTES } from '@common/constant/routeConstant';
import { ACADEMIC_TYPE } from '@common/type/CourseTypes';
import { useTranslation } from 'next-i18next';
import config from 'src/config';

import PurchaseButtonWithRemark from './purchaseButtonWithRemark';

import ConfirmEmailModal from '@components/atoms/dialog/confirmEmail';
import AuthorizeModal from '@components/share/modal/authorizeModal/AuthorizeModal';
import BaseModalWrapper from '@components/share/modal/baseModalWrapper';
import AvailableRegisterButton from '@components/templates/courseDetail/courseRegularBanner/purchaseButton/availableRegisterButton';
import EnrolledButton from '@components/templates/courseDetail/courseRegularBanner/purchaseButton/enrolledButton';
import EnrollmentExpiredButton from '@components/templates/courseDetail/courseRegularBanner/purchaseButton/enrollmentExpiredButton';
import EnrollmentExpiredDialog from '@components/templates/courseDetail/courseRegularBanner/purchaseButton/enrollmentExpiredDialog';
import { setEnrollCookie } from '@helpers/auth';
import { createGoogleAnalyticItem, sendAddToCardCourseDetail } from '@helpers/googleTagManager';
import useCourseDetail from '@hooks/course/useCourseDetail';
import useCourseEnrollment from '@hooks/course/useCourseEnrollment';
import useCreateUserEnrollFree from '@hooks/enrollment/useCreateUserEnrollFree';
import useDialogWithoutUI from '@hooks/useDialog';
import { useIdsProfile } from '@hooks/useIdsProfile';
import { useProfile } from '@hooks/useProfile';
import { CourseDetailEntity, CourseEntity, RegularCourseEnrollmentStatus } from '@redux/course/type/courseDetailType';

type CourseRegularBannerPurchaseButtonProps = {
  isFooter?: boolean;
  isDisableActionIcon?: boolean;
  courseDetailData: CourseDetailEntity;
  courseDetailSeo: CourseEntity;
};
const CourseRegularBannerPurchaseButton = ({
  isFooter = false,
  isDisableActionIcon = false,
  courseDetailData,
  courseDetailSeo,
}: CourseRegularBannerPurchaseButtonProps) => {
  const { t } = useTranslation('store_course');
  const { courseEnrollmentStatus } = useCourseEnrollment();
  const { idName, id } = courseDetailSeo;
  const { price } = useCourseDetail();
  const { profile } = useProfile();
  const { idsProfile } = useIdsProfile();
  const router = useRouter();
  const confirmEmailDialog = useDialogWithoutUI({});
  const enrollmentExpiredDialog = useDialogWithoutUI({});
  const authorizeDialog = useDialogWithoutUI({});
  const { createUserEnrollFree, isLoading, isSuccess, resetUserEnrollFree } = useCreateUserEnrollFree();

  const isFree = price?.regular?.salePrice === 0;

  const sendEvenToGtm = () => {
    if (courseDetailData) {
      const ga4Items = createGoogleAnalyticItem({
        courseDetail: courseDetailData,
        academicType: ACADEMIC_TYPE.REGULAR,
        courseName: name!,
      });
      sendAddToCardCourseDetail({
        value: price?.regular?.salePrice || 0,
        currency: 'THB',
        items: [ga4Items],
      });
    }
  };

  const handleClickPurchase = async () => {
    if (!profile) {
      authorizeDialog.onOpen();
      return;
    }

    if (profile?.email.includes(profile.uid)) {
      confirmEmailDialog.onOpen();
      return;
    }

    if (isFree) {
      createUserEnrollFree(id);
      return;
    }

    router.push(ROUTES.COURSE.PAYMENT(idName));
  };

  const handleClickContinueLearning = () => {
    router.push(ROUTES.COURSE.LEARNING(idName));
  };

  useEffect(() => {
    if (!isSuccess) return;
    resetUserEnrollFree();
    router.push(ROUTES.COURSE.LEARNING(idName));
  }, [idName, isSuccess, router]);

  if (!courseEnrollmentStatus) return null;

  const courseEnrollmentStatusButton: Record<RegularCourseEnrollmentStatus, JSX.Element> = {
    [RegularCourseEnrollmentStatus.ENROLLMENT_EXPIRED]: (
      <EnrollmentExpiredButton
        onClick={enrollmentExpiredDialog.onOpen}
        isFooter={isFooter}
        isDisableActionIcon={isDisableActionIcon}
        courseDetailSeo={courseDetailSeo}
      />
    ),
    [RegularCourseEnrollmentStatus.ENROLLED]: (
      <EnrolledButton
        courseDetailSeo={courseDetailSeo}
        onClick={handleClickContinueLearning}
        isFooter={isFooter}
        isDisableActionIcon={isDisableActionIcon}
      />
    ),
    [RegularCourseEnrollmentStatus.UNPUBLISHED]: (
      <PurchaseButtonWithRemark
        remarkText={t('course_detail.header_banner.purchase.unpublished.button.text')}
        isFooter={isFooter}
        isDisableActionIcon={isDisableActionIcon}
        courseDetailSeo={courseDetailSeo}
      />
    ),
    [RegularCourseEnrollmentStatus.PUBLISHED]: (
      <PurchaseButtonWithRemark
        remarkText={t('course_detail.header_banner.purchase.published.button.text')}
        isFooter={isFooter}
        isDisableActionIcon={isDisableActionIcon}
        courseDetailSeo={courseDetailSeo}
      />
    ),
    [RegularCourseEnrollmentStatus.AVAILABLE_REGISTER]: (
      <AvailableRegisterButton
        isFooter={isFooter}
        isFree={isFree}
        isDisableActionIcon={isDisableActionIcon}
        isLoading={isLoading || isSuccess}
        onClick={handleClickPurchase}
        courseDetailSeo={courseDetailSeo}
      />
    ),
  };

  const currentUrl = new URL(router.asPath, config.APP_DOMAIN);
  const onAuthorize = () => {
    setEnrollCookie(currentUrl.toString());
  };
  return (
    <>
      {courseEnrollmentStatusButton[courseEnrollmentStatus as RegularCourseEnrollmentStatus]}
      <BaseModalWrapper
        open={confirmEmailDialog.isOpened}
        onClose={confirmEmailDialog.onClose}
        disabledBackDrop
        disabledEscapeKeyDown
      >
        <ConfirmEmailModal citizenId={idsProfile?.citizenId} dialog={confirmEmailDialog} hideCloseButton />
      </BaseModalWrapper>
      <EnrollmentExpiredDialog
        open={enrollmentExpiredDialog.isOpened}
        onClose={enrollmentExpiredDialog.onClose}
        onConfirm={handleClickPurchase}
      />
      <AuthorizeModal
        open={authorizeDialog.isOpened}
        onClose={authorizeDialog.onClose}
        currentUrl={currentUrl.toString()}
        onAuthorize={onAuthorize}
      />
    </>
  );
};

export default CourseRegularBannerPurchaseButton;
